// AuctionContext.js
// Single source of truth for auction state machine.
// All components subscribe here — nothing talks directly.
//
// States:  standby | reveal | bidding | sold

import { createContext, useContext, useReducer, useRef, useCallback } from 'react';

const AuctionContext = createContext(null);

const INITIAL = {
  stage:         'standby',  // standby | reveal | bidding | sold
  player:        null,
  bid:           0,
  bidHistory:    [],
  teamBudgets:   [],
  recentSales:   [],
  sets:          [],
  currentSetNum: 1,
  totalSold:     0,
  totalUnsold:   0,
  totalPlayers:  0,
  remaining:     0,
  highestSale:   0,
  totalSpent:    0,
  sessionStatus: 'pending',
  lastBidAt:     null,
  soldAt:        null,
};

function reducer(state, action) {
  switch (action.type) {

    case 'SYNC': {
      const { auctionState } = action;
      if (!auctionState) return state;

      const recentSales  = auctionState.recent_results  || [];
      const sets         = auctionState.sets_progress   || [];
      const teamBudgets  = auctionState.team_budgets    || [];
      const currentPlayer = auctionState.current_player || null;
      const bid          = auctionState.current_bid     || 0;
      const stats        = auctionState.stats           || {};
      // Use real stats from API — recent_results is only last 8
      const totalSold      = stats.sold          || 0;
      const totalUnsold    = stats.unsold        || 0;
      const totalPlayers   = stats.total_players || sets.reduce((a, s) => a + (s.total || 0), 0);
      const remaining      = stats.remaining     || Math.max(0, totalPlayers - totalSold - totalUnsold);
      const highestSale    = stats.highest_sale  || 0;
      const totalSpent     = stats.total_spent   || 0;

      // Determine stage
      let stage = state.stage;
      const playerChanged = currentPlayer?.game_name !== state.player?.game_name;
      const bidChanged    = bid > state.bid;
      const playerSold    = currentPlayer?.status === 'sold';
      const playerUnsold  = currentPlayer?.status === 'unsold';

      if (!currentPlayer) {
        stage = 'standby';
      } else if (playerSold) {
        stage = 'sold';
      } else if (playerUnsold) {
        stage = 'unsold';
      } else if (playerChanged) {
        stage = 'reveal';
      } else if (bidChanged) {
        stage = 'bidding';
      } else if (stage === 'reveal' && !playerChanged) {
        stage = 'bidding';
      }

      // Build bid history
      let bidHistory = state.bidHistory;
      if (playerChanged) {
        bidHistory = [{ pts: currentPlayer?.base_price || 0, label: 'Start Price', time: '00:00' }];
      } else if (bidChanged && bid > 0) {
        bidHistory = [
          { pts: bid, label: 'Current Bid', time: 'LIVE' },
          ...state.bidHistory,
        ].slice(0, 6);
      }

      return {
        ...state,
        stage,
        player:        currentPlayer,
        bid,
        bidHistory,
        teamBudgets,
        recentSales,
        sets,
        currentSetNum: auctionState.current_set || 1,
        totalSold,
        totalUnsold,
        totalPlayers,
        remaining,
        highestSale,
        totalSpent,
        sessionStatus: auctionState.status || 'pending',
        lastBidAt:     bidChanged ? Date.now() : state.lastBidAt,
      };
    }

    case 'SOLD':
      return { ...state, stage: 'sold', soldAt: Date.now() };

    case 'STANDBY':
      return { ...state, stage: 'standby', player: null, bid: 0, bidHistory: [] };

    default:
      return state;
  }
}

export function AuctionProvider({ children, auctionState, teamLogoCache = {} }) {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const prevSyncRef = useRef(null);

  // Sync from poller — only when data changes
  // Inject cached logos into teamBudgets so we don't need them in every poll
  if (auctionState && auctionState !== prevSyncRef.current) {
    prevSyncRef.current = auctionState;
    const enriched = {
      ...auctionState,
      team_budgets: (auctionState.team_budgets || []).map(tb => ({
        ...tb,
        team_logo: teamLogoCache[tb.team_id] || tb.team_logo || null,
      })),
    };
    dispatch({ type: 'SYNC', auctionState: enriched });
  }

  const markSold = useCallback(() => dispatch({ type: 'SOLD' }), []);

  return (
    <AuctionContext.Provider value={{ ...state, markSold }}>
      {children}
    </AuctionContext.Provider>
  );
}

export const useAuction = () => {
  const ctx = useContext(AuctionContext);
  if (!ctx) throw new Error('useAuction must be inside AuctionProvider');
  return ctx;
};